import * as d3 from 'd3';
import { Component, h } from 'preact';

import { DEMOCRAT, REPUBLICAN } from '../colors';

class BalancePower extends Component {
  constructor() {
    super();

    this.state = {
      width: null,
      height: 80,
      bodyWidth: null,
      breakPoint: 544,
    };

    this.createGraphic = this.createGraphic.bind(this);
    this.redrawGraphic = this.redrawGraphic.bind(this);
    this.getContainerWidth = this.getContainerWidth.bind(this);
  }

  componentDidMount() {
    window.addEventListener('resize', () => {
      this.redrawGraphic();
    });
  }

  createGraphic(className, pastData, currentData) {
    let { width, bodyWidth } = this.getContainerWidth(className);

    let { height, breakPoint } = this.state;
    let prevdata = [
      {
        start: pastData.republican,
        end: pastData.republican + pastData.democrat,
        color: DEMOCRAT,
        party: 'Democrats',
        seats: pastData.democrat,
      },
      {
        start: 0,
        end: pastData.republican,
        color: REPUBLICAN,
        party: 'Republicans',
        seats: pastData.republican,
      },
    ];

    let currentElection = [
      {
        start:
          currentData.republican +
          currentData.uncontested_republican +
          currentData.vacant +
          currentData.democrat,
        startText:
          currentData.republican +
          currentData.uncontested_republican +
          currentData.vacant,
        end:
          currentData.republican +
          currentData.uncontested_republican +
          currentData.vacant +
          currentData.democrat +
          currentData.uncontested_democrat,
        color: '#113a85',
        party: 'Uncontested-Democrats',
        seats: currentData.uncontested_democrat,
      },
      {
        start:
          currentData.republican +
          currentData.uncontested_republican +
          currentData.vacant,
        startText:
          currentData.republican +
          currentData.uncontested_republican +
          currentData.vacant,
        end:
          currentData.republican +
          currentData.uncontested_republican +
          currentData.vacant +
          currentData.democrat,
        color: DEMOCRAT,
        party: 'Democrats',
        seats: currentData.democrat + currentData.uncontested_democrat,
      },
      {
        start: currentData.republican + currentData.uncontested_republican,
        end:
          currentData.republican +
          currentData.uncontested_republican +
          currentData.vacant,
        color: '#bdbdbd',
        party: 'Up for Grabs',
        seats: currentData.vacant,
      },
      {
        start: currentData.uncontested_republican,
        startText: 0,
        end: currentData.republican + currentData.uncontested_republican,
        color: REPUBLICAN,
        party: 'Republicans',
        seats: currentData.republican + currentData.uncontested_republican,
      },
      {
        start: 0,
        startText: 0,
        end: currentData.uncontested_republican,
        color: '#ab261b',
        party: 'Uncontested-Republicans',
        seats: currentData.uncontested_republican,
      },
    ];

    d3.selectAll(`.${className}-graphic-previous svg`).remove();

    d3.selectAll(`.${className}-graphic-current svg`).remove();

    // chart setup
    let margin = { top: 0, bottom: 0, right: 0, left: 0 };

    (width = width - margin.left - margin.right),
      (height = height - margin.top - margin.bottom);

    const isMobile = bodyWidth < breakPoint;

    let svg = d3
      .select(`.${className}-graphic-previous`)
      .append('svg')
      .attr('width', width + margin.left + margin.right)
      .attr('height', height + margin.top + margin.bottom);

    let svgCurrent = d3
      .select(`.${className}-graphic-current`)
      .append('svg')
      .attr('width', width + margin.left + margin.right)
      .attr('height', height + margin.top + margin.bottom + 85);

    let diagonalStripePattern = svgCurrent
      .append('defs')
      .append('pattern')
      .attr('id', 'diagonal-stripe')
      .attr('patternUnits', 'userSpaceOnUse')
      .attr('patternTransform', 'rotate(45 0 0)')
      .attr('width', 6)
      .attr('height', 6);

    diagonalStripePattern
      .append('rect')
      .attr('width', 6)
      .attr('height', 6)
      .style('fill', '#d9d9d9');

    diagonalStripePattern
      .append('line')
      .attr('x1', 0)
      .attr('y1', 0)
      .attr('x2', 0)
      .attr('y2', 6)
      .style('stroke', 'black')
      .style('stroke-width', 0.5);

    let g = svg
      .append('g')
      .attr('class', `${className}-graphic-past-group`)
      .attr('width', width)
      .attr('transform', `translate(${margin.left},${margin.top})`);

    let gCurrent = svgCurrent
      .append('g')
      .attr('class', `${className}-graphic-current-group`)
      .attr('width', width)
      .attr('transform', `translate(${margin.left},${margin.top})`);

    let x = d3.scaleLinear().domain([0, pastData.total]).range([0, width]);

    let xCurrent = d3
      .scaleLinear()
      .domain([0, currentData.total])
      .range([0, width]);

    let pastBar = g
      .selectAll('g')
      .data(prevdata)
      .enter()
      .append('g')
      .attr('transform', 'translate(0,' + 30 + ')');

    pastBar
      .append('rect')
      .attr('class', 'past-bar')
      .attr('width', (d) => x(d.end - d.start))
      .attr('x', (d) => x(d.start))
      .attr('fill', (d) => d.color)
      .attr('height', 20);

    g.selectAll('.bar-title')
      .data(prevdata)
      .enter()
      .append('text')
      .attr('class', (d) => `bar-title text-${d.party}`)
      .attr('text-anchor', 'start')
      .attr('x', (d, i) => (i == 1 ? x(d.start) : width - 110))
      .attr('y', 20)
      .style('fill', (d) => `${d.color}`)
      .attr('height', 10)
      .text((d) => `${d.seats} ${d.party}`);

    let currentBar = gCurrent
      .selectAll('g')
      .data(currentElection)
      .enter()
      .append('g')
      .attr('transform', 'translate(0,' + 60 + ')');

    currentBar
      .append('rect')
      .attr('class', (d) => {
        if (d.party == 'Up for Grabs') return 'current-bar dashed-line';
        else return 'current-bar';
      })
      .attr('width', (d) => xCurrent(d.end - d.start))
      .attr('x', (d) => xCurrent(d.start))
      .attr('fill', (d) => d.color)
      .attr('height', 20);

    let currentText = gCurrent
      .selectAll('.bar-title')
      .data(currentElection)
      .enter()
      .append('text')
      .filter(
        (d) =>
          d.party !== 'Up for Grabs' &&
          d.party !== 'Uncontested-Republicans' &&
          d.party !== 'Uncontested-Democrats'
      )
      .attr('class', (d) => `bar-title current-text-${className}-${d.party}`)
      .attr('text-anchor', 'start')
      .attr('x', (d, i) => (i == 1 ? xCurrent(d.startText) : width - 110))
      .attr('y', 30)
      .style('fill', (d) => `${d.color}`)
      .attr('height', 10)
      .text((d) => `${d.seats} ${d.party}`);

    // Text for uncontested seats

    let TextUncontested = gCurrent
      .selectAll('.bar-title-uncontested')
      .data(
        currentElection.sort(function (a, b) {
          if (a.party < b.party) {
            return -1;
          }
          if (a.party > b.party) {
            return 1;
          }
          return 0;
        })
      )
      .enter()
      .append('text')
      .filter(
        (d) =>
          d.party !== 'Up for Grabs' &&
          d.party !== 'Republicans' &&
          d.party !== 'Democrats'
      )
      .attr(
        'class',
        (d) => `bar-title-uncontested ${className}-uncontested-text-${d.party}`
      )
      .attr('text-anchor', 'start')
      .attr('y', 50)
      .style('fill', (d) => `${d.color}`)
      .attr('height', 10)
      .text((d, i) =>
        i == 1
          ? `${currentData.uncontested_republican} were uncontested`
          : `${currentData.uncontested_democrat} were uncontested`
      );

    let textWidth, textWidthUncontested;

    let textSelection = d3
      .select(`.current-text-${className}-Democrats`)
      .node();
    let textSelection2 = d3
      .select(`.${className}-uncontested-text-Uncontested-Democrats`)
      .node();

    if (textSelection) {
      textWidth = textSelection.getComputedTextLength();
      textWidthUncontested = textSelection2.getComputedTextLength();
    }

    currentText.attr('x', (d, i) =>
      i == 1 ? xCurrent(d.startText) : width - textWidth
    );
    TextUncontested.attr('x', (d, i) =>
      i == 1 ? xCurrent(d.startText) : width - textWidthUncontested
    );

    gCurrent
      .selectAll('.bar-title-contested')
      .data(currentElection)
      .enter()
      .append('text')
      .filter((d) => d.party == 'Up for Grabs')
      .attr('class', `bar-title-contested`)
      .attr('text-anchor', 'start')
      .attr('x', width / 3)
      .attr('y', className == 'us-house' ? height + 40 : height + 65)
      .style('fill', '#4a4a4a')
      .attr('height', 10)
      .text((d, i) =>
        currentData.vacant > 0
          ? `${currentData.vacant} seat${
              currentData.vacant > 1 ? 's' : ''
            } still up for grabs`
          : ``
      );

    if (className != 'us-house') {
      g.append('line')
        .attr('x1', x(currentData.halfway))
        .attr('x2', x(currentData.halfway))
        .attr('y1', 10)
        .attr('y2', height - 10)
        .attr('class', 'annotation-line');

      gCurrent
        .append('line')
        .attr('x1', xCurrent(currentData.halfway))
        .attr('x2', xCurrent(currentData.halfway))
        .attr('y1', 40)
        .attr('y2', height + 20)
        .attr('class', 'annotation-line');

      gCurrent
        .append('text')
        .attr('class', `text-majority`)
        .attr('text-anchor', 'start')
        .attr('x', xCurrent(currentData.halfway) - 8)
        .attr('y', height + 35)
        .style('fill', '#000')
        .text((d, i) => `${currentData.halfway}`);
    }
  }

  getContainerWidth(className) {
    let base;

    const body = d3.select('body');

    base = d3.select(`.${className}-balance-graphic`);

    const width = base.node().offsetWidth;
    const bodyWidth = body.node().offsetWidth;

    return { width, bodyWidth };
  }

  redrawGraphic() {
    const { className, pastData, currentData } = this.props;

    d3.selectAll(`.${className}-graphic-previous svg`).remove();
    d3.selectAll(`.${className}-graphic-current svg`).remove();

    let { width, bodyWidth } = this.getContainerWidth(className);

    this.setState({ width: width, bodyWidth: bodyWidth });
    this.createGraphic(className, pastData, currentData);
  }

  componentWillUnmount() {
    window.addEventListener('resize', () => {
      this.redrawGraphic();
    });
  }

  render() {
    const { className, pastData, currentData, chamber } = this.props;

    this.createGraphic(className, pastData, currentData);

    const chamberText =
      chamber == 'U.S. House'
        ? 'Texas delegation in'
        : 'the balance of power in';

    return (
      <div id='balance-graphics'>
        <p class='t-copy copy--narrative'>
          {`For the last two years, ${chamberText} the ${chamber} has looked like this: `}
        </p>
        <div class={`${className}-graphic-previous`}></div>
        <p class='t-copy copy--narrative'>
          {`Results are in. Here is what the breakdown will look like:`}
        </p>
        <div class={`${className}-graphic-current`}></div>
      </div>
    );
  }
}

export default BalancePower;
